import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { api, naira, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, ScanLine, Store, Wallet, Clock, CheckCircle2, User, ArrowRight, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";
import { ShareStory } from "@/components/ShareStory";

function Metric({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-emerald-100 bg-white p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500 font-medium">{label}</span>
        <Icon className={`w-4 h-4 ${accent || "text-emerald-500"}`} />
      </div>
      <div className="mt-2 font-bold text-2xl text-emerald-950 tabular-nums">{value}</div>
    </div>
  );
}

function AccountEditor({ v, onSaved }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [acct, setAcct] = useState("");
  const [saving, setSaving] = useState(false);
  const start = () => { setName(v?.pending_account_name || v?.account_name || ""); setAcct(v?.pending_account_number || v?.moniepoint_account_number || ""); setOpen(true); };
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { const { data } = await api.post("/vendors/payment-account", { account_name: name, moniepoint_account_number: acct }); toast.success(data.message); setOpen(false); onSaved(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-4 rounded-2xl border border-emerald-100 bg-white p-4" data-testid="vendor-account-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm"><span className="text-gray-500">Settlement account:</span> <b data-testid="vendor-account-name">{v?.account_name || "Name not set"}</b> · Moniepoint •••• {String(v?.moniepoint_account_number || "").slice(-4)}
          {v?.pending_account_name && <div className="text-xs text-amber-700" data-testid="vendor-account-pending">Change requested: {v.pending_account_name} — awaiting TMF admin re-approval</div>}
        </div>
        {!open && <Button size="sm" variant="outline" data-testid="edit-account-btn" onClick={start}>Add / correct account name</Button>}
      </div>
      {open && (
        <form onSubmit={save} className="mt-3 grid gap-3 sm:grid-cols-3 items-end">
          <div><Label>Vendor Account Name</Label><Input data-testid="edit-account-name" value={name} onChange={(e) => setName(e.target.value)} required minLength={3} className="mt-1 h-10" /></div>
          <div><Label>Moniepoint Account Number</Label><Input data-testid="edit-account-number" value={acct} onChange={(e) => setAcct(e.target.value.replace(/\D/g, ""))} required minLength={10} maxLength={10} className="mt-1 h-10 font-mono" /></div>
          <div className="flex gap-2"><Button type="submit" data-testid="save-account-btn" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit for approval"}</Button><Button type="button" variant="ghost" data-testid="cancel-account-btn" onClick={() => setOpen(false)}>Cancel</Button></div>
          <p className="sm:col-span-3 text-[11px] text-amber-700">Saving pauses redemptions until a TMF admin re-approves your account.</p>
        </form>
      )}
    </div>
  );
}

function LandmarkEditor({ v, onSaved }) {
  const [val, setVal] = useState(null);
  const save = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/vendors/landmark", { landmark: val }); toast.success(data.message); setVal(null); onSaved(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="mt-3 rounded-2xl border border-emerald-100 bg-white p-4 text-sm" data-testid="vendor-landmark-card">
      {val === null ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div><span className="text-gray-500">Nearest landmark:</span> <b data-testid="vendor-landmark">{v?.landmark || "Not set"}</b></div>
          <Button size="sm" variant="outline" data-testid="edit-landmark-btn" onClick={() => setVal(v?.landmark || "")}>{v?.landmark ? "Edit landmark" : "Add landmark"}</Button>
        </div>
      ) : (
        <form onSubmit={save} className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[220px]"><Label>Nearest Landmark</Label><Input data-testid="edit-landmark-input" value={val} onChange={(e) => setVal(e.target.value)} maxLength={80} placeholder="e.g. Opposite Shoprite" className="mt-1 h-10" /></div>
          <Button type="submit" data-testid="save-landmark-btn" className="bg-emerald-600 hover:bg-emerald-700">Save</Button>
          <Button type="button" variant="ghost" data-testid="cancel-landmark-btn" onClick={() => setVal(null)}>Cancel</Button>
        </form>
      )}
    </div>
  );
}

export default function VendorPortal() {
  const [dash, setDash] = useState(null);
  const [code, setCode] = useState("");
  const [lookup, setLookup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);

  const load = () => api.get("/vendors/dashboard").then((r) => setDash(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  // Auto-poll a PROCESSING redemption until Monnify confirms (or fails), so it flips on screen.
  useEffect(() => {
    const ref = receipt?.redemption?.internal_reference;
    if (!receipt || receipt.paid || !ref) return;
    let tries = 0;
    const id = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/redemptions/${ref}/status`);
        if (data.paid) { setReceipt((p) => ({ ...p, paid: true, status: "REDEEMED", redemption: { ...p.redemption, provider_reference: data.provider_reference } })); toast.success("Payment confirmed — voucher redeemed"); load(); clearInterval(id); }
        else if (data.failed) { setReceipt((p) => ({ ...p, status: "PAYMENT_FAILED" })); toast.error("Payment failed — voucher released for re-redemption"); load(); clearInterval(id); }
      } catch (e) { /* keep polling */ }
      if (tries >= 20) clearInterval(id);
    }, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipt?.redemption?.internal_reference, receipt?.paid]);

  const downloadReceipt = (r) => {
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>TMF Settlement Receipt ${r.ref}</title>` +
      `<style>body{font-family:system-ui,Arial,sans-serif;color:#064e3b;max-width:520px;margin:40px auto;padding:24px;border:1px solid #d1fae5;border-radius:16px}h1{font-size:18px}table{width:100%;border-collapse:collapse;margin-top:16px}td{padding:8px 0;border-bottom:1px solid #ecfdf5;font-size:14px}td:last-child{text-align:right;font-weight:600}.amt{font-size:28px;font-weight:700;color:#059669}.muted{color:#6b7280;font-size:12px}</style></head>` +
      `<body><h1>TEXT-ME-FOOD — Settlement Receipt</h1><div class="amt">${naira(r.amount)}</div><table>` +
      `<tr><td>TMF Voucher ID</td><td>${r.voucher || "—"}</td></tr>` +
      `<tr><td>TMF Reference</td><td>${r.ref || "—"}</td></tr>` +
      `<tr><td>Monnify Reference</td><td>${r.monnify || "—"}</td></tr>` +
      `<tr><td>Paid to</td><td>${r.vendor || "—"}</td></tr>` +
      `<tr><td>Account</td><td>${r.acct || "—"}</td></tr>` +
      `<tr><td>Date</td><td>${r.when || "—"}</td></tr>` +
      `<tr><td>Status</td><td>PAID</td></tr></table>` +
      `<p class="muted">System-generated settlement receipt from Text-Me-Food.</p></body></html>`;
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    const a = document.createElement("a"); a.href = url; a.download = `TMF-Receipt-${r.ref || "receipt"}.html`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const doLookup = async () => {
    setBusy(true); setReceipt(null);
    try {
      const { data } = await api.post("/redemptions/lookup", { code });
      setLookup(data);
    } catch (e) { toast.error(formatApiError(e)); setLookup(null); }
    finally { setBusy(false); }
  };

  const doRedeem = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/redemptions", { code });
      setReceipt(data);
      if (data.paid) toast.success(`Redeemed ${naira(data.amount)} — paid to your account`);
      else toast.success(`Redemption submitted for ${naira(data.amount)} — payment is processing, confirmation will follow`);
      setLookup(null); setCode(""); load();
    } catch (e) { toast.error(formatApiError(e)); setLookup(null); load(); }
    finally { setBusy(false); }
  };

  if (!dash) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;
  const m = dash.metrics;
  const v = dash.vendor;
  const verified = v?.account_verified && v?.status === "verified";

  return (
    <div>
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-10">
        <span className="text-xs font-mono uppercase tracking-widest text-emerald-700">Vendor Terminal</span>
        <h1 className="mt-2 font-display font-bold text-3xl text-emerald-950">{v?.business_name}</h1>
        <div className="text-sm text-gray-500 flex items-center gap-3 mt-1">
          <span className="font-mono">{v?.vendor_id}</span><span>·</span><span>{v?.lga}, {v?.state}</span>
        </div>

        {!verified && (
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="vendor-unverified-banner">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div><b>Your payment account is awaiting TMF verification.</b> You can redeem vouchers once an administrator approves your Moniepoint settlement account ({v?.moniepoint_account_number ? `•••• ${String(v.moniepoint_account_number).slice(-4)}` : "not set"}).</div>
          </div>
        )}
        {verified && (
          <div className="mt-4 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800" data-testid="vendor-verified-banner">
            <CheckCircle2 className="w-4 h-4" /> Settlements paid to <b>{v?.account_name || "your Moniepoint account"}</b> · •••• {String(v?.moniepoint_account_number || "").slice(-4)} <Badge className="ml-1 bg-emerald-600 text-white">VERIFIED</Badge>
          </div>
        )}
        <AccountEditor v={v} onSaved={load} />
        <LandmarkEditor v={v} onSaved={load} />

        <div className="mt-6 grid gap-4 grid-cols-2 lg:grid-cols-4">
          <Metric icon={CheckCircle2} label="Today's Vouchers" value={m.today_transactions} />
          <Metric icon={Wallet} label="Today's Value" value={naira(m.today_value)} />
          <Metric icon={Clock} label="Pending Settlement" value={naira(m.pending_settlement)} accent="text-amber-500" />
          <Metric icon={Store} label="Payments Received" value={naira(m.settled_amount)} />
        </div>

        <div className="mt-8 grid lg:grid-cols-5 gap-6">
          {/* Redemption engine */}
          <div className="lg:col-span-3 rounded-3xl border-2 border-emerald-200 bg-white p-6 lg:p-8">
            <h2 className="font-display font-bold text-xl text-emerald-950 flex items-center gap-2"><ScanLine className="w-5 h-5 text-emerald-600" /> Verify & Redeem Benefit</h2>
            <p className="mt-1 text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2 border border-emerald-100">Enter the beneficiary's TMF Voucher ID. On REDEEM, the full benefit is paid directly to your registered account and the voucher is marked redeemed.</p>

            {!lookup && !receipt && (
              <div className="mt-6 space-y-4">
                <div>
                  <Label>TMF Voucher ID / Code</Label>
                  <Input data-testid="redeem-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="CBEP-000001 or TMF-CODE" className="mt-1.5 h-14 font-mono text-lg" disabled={!verified} />
                </div>
                <Button data-testid="redeem-lookup-btn" onClick={doLookup} disabled={busy || !code || !verified} className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-xl h-12">
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Verify Voucher <ArrowRight className="w-4 h-4 ml-2" /></>}
                </Button>
              </div>
            )}

            {lookup && (
              <div className="mt-6 space-y-5" data-testid="voucher-verified-panel">
                <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5">
                  <div className="text-[11px] uppercase tracking-widest text-emerald-600 font-semibold">Voucher Verified</div>
                  <div className="mt-2 flex items-center gap-2 text-emerald-800"><User className="w-4 h-4" /> <span className="font-semibold">{lookup.beneficiary?.full_name || "Beneficiary"}</span></div>
                  <div className="mt-1 text-sm text-emerald-700 font-mono">{lookup.beneficiary?.tmf_id} · {lookup.voucher.beneficiary_phone}</div>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-center">
                    <div><div className="text-xs text-gray-500">Voucher ID</div><div className="font-semibold text-sm font-mono text-emerald-800">{lookup.voucher.tmf_voucher_id || lookup.voucher.code}</div></div>
                    <div><div className="text-xs text-gray-500">Programme</div><div className="font-semibold text-sm">{lookup.voucher.programme_code}</div></div>
                  </div>
                  <div className="mt-3 rounded-xl bg-white border border-emerald-200 p-4 text-center">
                    <div className="text-xs text-gray-500">Benefit to be paid to you</div>
                    <div data-testid="settlement-amount" className="font-bold text-3xl text-emerald-700 tabular-nums">{naira(lookup.settlement_amount)}</div>
                  </div>
                  <div className="mt-2 text-center text-xs text-gray-500">Destination: {lookup.vendor?.account_name || v?.business_name} · {lookup.vendor?.account_masked}</div>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setLookup(null)} className="flex-1" data-testid="redeem-cancel-btn">Cancel</Button>
                  <Button data-testid="redeem-confirm-btn" onClick={doRedeem} disabled={busy} className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-12 text-base">
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : `REDEEM ${naira(lookup.settlement_amount)}`}
                  </Button>
                </div>
              </div>
            )}

            {receipt && (
              <div className="mt-6 rounded-2xl bg-emerald-950 p-6 text-white tmf-rise" data-testid="redeem-receipt">
                <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                <h3 className="mt-3 font-display font-bold text-xl">{receipt.paid ? "Redemption Successful" : "Payment Processing"}</h3>
                <p className="text-xs text-emerald-200 mt-1">{receipt.paid ? "Payment has been sent to your registered account." : "Your payment is being processed by Monnify. The voucher stays locked until payment is confirmed — no need to retry. You'll see it move to Paid shortly."}</p>
                <div className="mt-3 font-mono text-sm space-y-1 text-emerald-100">
                  <div>Ref: <span className="text-white">{receipt.redemption?.internal_reference}</span></div>
                  <div>Amount: <span className="text-white">{naira(receipt.amount)}</span></div>
                  <div>Status: <span className="text-white">{receipt.status}{receipt.redemption?.simulated ? " · SIMULATED" : ""}</span></div>
                </div>
                <div className="mt-4 flex gap-2">
                  {receipt.paid && <Button data-testid="download-receipt-btn" onClick={() => downloadReceipt({ ref: receipt.redemption?.internal_reference, voucher: receipt.redemption?.tmf_voucher_id || receipt.redemption?.voucher_code, amount: receipt.amount, monnify: receipt.redemption?.provider_reference, vendor: v?.business_name, acct: v?.account_name, when: new Date().toLocaleString() })} className="bg-emerald-500 hover:bg-emerald-600"><Download className="w-4 h-4 mr-1" />Download receipt</Button>}
                  <Button data-testid="redeem-new-btn" onClick={() => setReceipt(null)} className="bg-white text-emerald-800 hover:bg-emerald-50">New Redemption</Button>
                </div>
              </div>
            )}
          </div>

          {/* Recent transactions */}
          <div className="lg:col-span-2 rounded-3xl border border-emerald-100 bg-white p-6">
            <h3 className="font-display font-semibold text-lg text-emerald-950">Recent Transactions</h3>
            <div className="mt-4 space-y-3 max-h-[420px] overflow-auto">
              {dash.transactions.length === 0 && <p className="text-sm text-gray-500">No transactions yet.</p>}
              {dash.transactions.map((t) => (
                <div key={t.id} className="flex items-center justify-between border-b border-gray-100 pb-2">
                  <div>
                    <div className="font-mono text-xs text-gray-400">{t.txn_id}</div>
                    <div className="text-sm">{t.tmf_id}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-emerald-700">{naira(t.amount)}</div>
                    <div className="flex items-center gap-1.5 justify-end">
                      <Badge className={t.settlement_status === "settled" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{t.settlement_status === "settled" ? "Paid" : t.settlement_status}</Badge>
                      {t.settlement_status === "settled" && <button data-testid={`dl-receipt-${t.txn_id}`} title="Download receipt" onClick={() => downloadReceipt({ ref: t.txn_id, voucher: t.tmf_voucher_id || t.voucher_code, amount: t.amount, monnify: t.monnify_reference, vendor: v?.business_name, acct: v?.account_name, when: t.timestamp })} className="text-emerald-600 hover:text-emerald-800"><Download className="w-3.5 h-3.5" /></button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <ShareStory defaultName={dash?.vendor?.business_name} />
      </div>
    </div>
  );
}
